// ChatApp - chat temps réel Webdesk (WebSocket)
import React, { useEffect, useRef, useState } from 'react';
import { Paper, Typography, Box, TextField, Button, List, ListItem, ListItemText, IconButton, Dialog, DialogTitle, DialogContent } from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import CallIcon from '@mui/icons-material/Call';

// Authentification : récupère le token d'authentification depuis localStorage
function getToken() {
  return window.localStorage.getItem('token');
}
// Ajoute le token dans l'URL WebSocket
function getWsUrl() {
  const token = getToken();
  return (window.location.protocol === 'https:' ? 'wss://' : 'ws://') + window.location.hostname + ':8000/ws/chat' + (token ? `?token=${encodeURIComponent(token)}` : '');
}

export default function ChatApp() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [username, setUsername] = useState(() => window.localStorage.getItem('currentUserName') || "Anonyme");
  const [users, setUsers] = useState([]); // Liste des utilisateurs connectés
  const [recipient, setRecipient] = useState(''); // Destinataire sélectionné (uuid ou username)
  const [userSearch, setUserSearch] = useState(''); // Terme de recherche utilisateur
  const ws = useRef(null);
  const listRef = useRef();

  // Ajout des hooks pour WebRTC
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  const [isVideoCall, setIsVideoCall] = useState(false);
  const localVideoRef = useRef();
  const remoteVideoRef = useRef();
  const peerConnectionRef = useRef();
  const [callTarget, setCallTarget] = useState(null);
  const [callError, setCallError] = useState("");

  // --- Vérification expiration JWT ---
  function isTokenExpired(token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (!payload.exp) return false;
      return Date.now() >= payload.exp * 1000;
    } catch (e) {
      return true;
    }
  }

  const [tokenExpired, setTokenExpired] = useState(false);
  const [tokenState, setTokenState] = useState(getToken());

  // Rafraîchit le tokenState si le token change dans localStorage (par exemple, renouvellement ailleurs)
  useEffect(() => {
    function syncToken() {
      setTokenState(getToken());
    }
    window.addEventListener('storage', syncToken);
    return () => window.removeEventListener('storage', syncToken);
  }, []);

  // --- Vérifie l'expiration du token à chaque changement ---
  useEffect(() => {
    if (!tokenState) {
      setTokenExpired(true);
      return;
    }
    if (isTokenExpired(tokenState)) {
      setTokenExpired(true);
      return;
    }
    setTokenExpired(false);
  }, [tokenState]);

  // --- Rafraîchissement du token tant que la connexion WebSocket est active ---
  useEffect(() => {
    if (tokenExpired) return;
    let refreshInterval;
    function refreshTokenIfNeeded() {
      const t = getToken();
      setTokenState(t);
      if (isTokenExpired(t)) {
        setTokenExpired(true);
      }
    }
    // Vérifie toutes les 15 secondes (plus fréquent)
    refreshInterval = setInterval(refreshTokenIfNeeded, 15000);
    return () => clearInterval(refreshInterval);
  }, [tokenExpired]);

  // --- Connexion WebSocket qui se reconnecte si le token change ou est rafraîchi ---
  useEffect(() => {
    if (tokenExpired) return;
    let wsInstance;
    let reconnectTimeout;
    function connectWs() {
      wsInstance = new window.WebSocket(getWsUrl());
      ws.current = wsInstance;
      wsInstance.onopen = () => {
        // Demande explicite la liste des utilisateurs connectés dès l'ouverture
        wsInstance.send(JSON.stringify({ type: 'user_list_request' }));
      };
      wsInstance.onmessage = (event) => {
        const msg = JSON.parse(event.data);
        if (msg.type === 'user_list') {
          setUsers(msg.users);
        } else if (msg.type === 'webrtc-offer' && msg.sender !== username) {
          // Réception d'une offre WebRTC
          handleReceiveOffer(msg);
        } else if (msg.type === 'webrtc-answer' && msg.sender !== username) {
          handleReceiveAnswer(msg);
        } else if (msg.type === 'webrtc-ice' && msg.sender !== username) {
          handleReceiveIce(msg);
        } else {
          setMessages(msgs => [...msgs, msg]);
        }
      };
      wsInstance.onclose = () => {
        setMessages(msgs => [...msgs, { sender: 'SYSTEM', content: 'Déconnecté du serveur de chat.', timestamp: Date.now() }]);
        // Tente de se reconnecter si la session du bureau est toujours ouverte
        if (!tokenExpired) {
          reconnectTimeout = setTimeout(connectWs, 2000);
        }
      };
    }
    connectWs();
    return () => {
      if (wsInstance) wsInstance.close();
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
    };
  }, [tokenExpired, tokenState, username]);

  useEffect(() => {
    // Scroll to bottom
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = () => {
    if (!input.trim()) return;
    if (ws.current && ws.current.readyState === 1) {
      ws.current.send(JSON.stringify({ sender: username, content: input, recipient }));
      setInput("");
    } else {
      setMessages(msgs => [...msgs, { sender: 'SYSTEM', content: 'Connexion au serveur de chat non établie. Réessayez dans quelques secondes.', timestamp: Date.now() }]);
    }
  };

  // --- Appel vidéo/audio ---
  const startCall = async (target, video = true) => {
    setCallError("");
    try {
      setCallTarget(target);
      setIsVideoCall(video);
      setCallDialogOpen(true);
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video, audio: true });
      } catch (err) {
        if (video) {
          // Fallback audio-only si webcam absente
          try {
            stream = await navigator.mediaDevices.getUserMedia({ video: false, audio: true });
            setIsVideoCall(false);
            setCallError("Webcam non trouvée, passage en mode audio uniquement.");
          } catch (err2) {
            setCallDialogOpen(false);
            setCallError("Aucun périphérique audio/vidéo trouvé ou accès refusé.");
            return;
          }
        } else {
          setCallDialogOpen(false);
          setCallError("Aucun périphérique audio trouvé ou accès refusé.");
          return;
        }
      }
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;
      const pc = new RTCPeerConnection();
      peerConnectionRef.current = pc;
      stream.getTracks().forEach(track => pc.addTrack(track, stream));
      pc.onicecandidate = (event) => {
        if (event.candidate) {
          ws.current.send(JSON.stringify({ type: 'webrtc-ice', candidate: event.candidate, recipient: target }));
        }
      };
      pc.ontrack = (event) => {
        if (remoteVideoRef.current) remoteVideoRef.current.srcObject = event.streams[0];
      };
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      ws.current.send(JSON.stringify({ type: 'webrtc-offer', offer, recipient: target }));
    } catch (err) {
      setCallDialogOpen(false);
      setCallError("Erreur lors de l'initialisation de l'appel : " + (err && err.message ? err.message : err));
    }
  };

  const handleReceiveOffer = async (msg) => {
    setCallTarget(msg.sender);
    setIsVideoCall(true);
    setCallDialogOpen(true);
    const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    if (localVideoRef.current) localVideoRef.current.srcObject = stream;
    const pc = new RTCPeerConnection();
    peerConnectionRef.current = pc;
    stream.getTracks().forEach(track => pc.addTrack(track, stream));
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        ws.current.send(JSON.stringify({ type: 'webrtc-ice', candidate: event.candidate, recipient: msg.sender }));
      }
    };
    pc.ontrack = (event) => {
      if (remoteVideoRef.current) remoteVideoRef.current.srcObject = event.streams[0];
    };
    await pc.setRemoteDescription(new RTCSessionDescription(msg.offer));
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    ws.current.send(JSON.stringify({ type: 'webrtc-answer', answer, recipient: msg.sender }));
  };

  const handleReceiveAnswer = async (msg) => {
    if (peerConnectionRef.current) {
      await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(msg.answer));
    }
  };

  const handleReceiveIce = async (msg) => {
    if (peerConnectionRef.current) {
      try {
        await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(msg.candidate));
      } catch (e) {}
    }
  };

  const endCall = () => {
    setCallDialogOpen(false);
    setCallTarget(null);
    setIsVideoCall(false);
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    if (localVideoRef.current && localVideoRef.current.srcObject) {
      localVideoRef.current.srcObject.getTracks().forEach(track => track.stop());
      localVideoRef.current.srcObject = null;
    }
    if (remoteVideoRef.current && remoteVideoRef.current.srcObject) {
      remoteVideoRef.current.srcObject.getTracks().forEach(track => track.stop());
      remoteVideoRef.current.srcObject = null;
    }
  };

  if (tokenExpired) {
    return (
      <Paper elevation={4} sx={{ p: 3, maxWidth: 480, margin: 'auto' }}>
        <Typography variant="h6" align="center" gutterBottom>
          Session expirée
        </Typography>
        <Typography align="center" color="error" sx={{ mb: 2 }}>
          Votre session a expiré. Veuillez vous reconnecter pour continuer à utiliser le chat.
        </Typography>
        {/* Ajouter ici un bouton pour relancer la connexion ou ouvrir la page de login */}
      </Paper>
    );
  }

  return (
    <Paper elevation={4} sx={{ p: 3, maxWidth: 480, margin: 'auto' }}>
      <Typography variant="h6" align="center" gutterBottom>
        Chat multi-utilisateur
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Box sx={{ width: 180 }}>
          <Typography variant="subtitle2">Utilisateurs connectés</Typography>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Rechercher..."
            value={userSearch}
            onChange={e => setUserSearch(e.target.value)}
            fullWidth
            sx={{ mb: 1 }}
          />
          <List dense sx={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #eee', borderRadius: 1 }}>
            {users.filter(u => (u.username || u.id || '').toLowerCase().includes(userSearch.toLowerCase())).map(u => (
              <ListItem
                key={u.id || u.username}
                button
                selected={recipient === (u.id || u.username)}
                onClick={() => setRecipient(u.id || u.username)}
                secondaryAction={
                  <>
                    <IconButton size="small" onClick={() => startCall(u.username || u.id, false)} title="Appel audio"><CallIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => startCall(u.username || u.id, true)} title="Appel vidéo"><VideocamIcon fontSize="small" /></IconButton>
                  </>
                }
              >
                <ListItemText primary={u.username || u.id} />
              </ListItem>
            ))}
          </List>
        </Box>
        <Box sx={{ flex: 1 }}>
          <List ref={listRef} sx={{ maxHeight: 200, overflowY: 'auto', mb: 2, bgcolor: '#f7f7ff' }}>
            {messages.map((msg, idx) => (
              <ListItem key={idx}>
                <ListItemText
                  primary={msg.sender + (msg.recipient ? ` → ${msg.recipient}` : '')}
                  secondary={msg.content}
                />
              </ListItem>
            ))}
          </List>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              variant="outlined"
              size="small"
              placeholder="Votre message..."
              value={input}
              fullWidth
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') sendMessage(); }}
            />
            <Button variant="contained" onClick={sendMessage}>Envoyer</Button>
          </Box>
        </Box>
      </Box>
      <Dialog open={callDialogOpen} onClose={endCall} maxWidth="md" fullWidth>
        <DialogTitle>{isVideoCall ? 'Appel vidéo' : 'Appel audio'} avec {callTarget}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
            <video ref={localVideoRef} autoPlay muted playsInline style={{ width: 200, borderRadius: 8, background: '#222' }} />
            <video ref={remoteVideoRef} autoPlay playsInline style={{ width: 200, borderRadius: 8, background: '#222' }} />
          </Box>
          <Button onClick={endCall} variant="contained" color="error" sx={{ mt: 2 }}>Raccrocher</Button>
        </DialogContent>
      </Dialog>
      {callError && (
        <Box sx={{ mt: 2 }}>
          <Typography color="error" align="center">{callError}</Typography>
        </Box>
      )}
    </Paper>
  );
}
